import { Action } from 'redux';
import * as _ from 'underscore';

import { ParsedMetricData } from '../../../metrics/models';
import { ExplorableItemType, isRecommendationType } from '../../../ts/models/ContentType';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IDataSourceInfo } from '../../../ts/models/IDataSourceInfo';
import { isMetricInfo } from '../../../ts/models/IMetricInfo';
import { IViewInfo } from '../../../ts/models/IViewInfo';
import { IExplorableItemTypeInfo } from '../../../ts/models/resourceInfoParts';
import { DataSources } from '../../../ts/resources/Datasources';
import { IMetricWithSummaryData } from '../../../ts/ServerApi/types/IMetricWithSummaryData';
import { ContentFavoriteAdded, ContentFavoriteRemoved, RecommendationHidden } from '../../actions/AppActionTypes';
import { isContentFavoriteAddedAction, isContentFavoriteRemovedAction, isRemoveFavoritedItemAction } from '../../actions/spaces/FavoritesActions';
import { isUpdateContentDetailAction } from '../../actions/spaces/GetContentDetailAction';
import { isRecommendationHiddenAction, isRecommendationUnhiddenAction } from '../../actions/spaces/HideRecommendationAction';
import {
  isSpaceContentsRequestFailedAction,
  isSpaceContentsRequestSucceededAction,
  isSpaceContentsSliceSucceededAction
} from '../../actions/spaces/RequestSpaceContentsActions';
import { isMetricsValueRequestSucceededAction } from '../../actions/spaces/UpdateMetricsActions';

function findIndexOfContent(content: ReadonlyArray<IExplorableItemTypeInfo>, type: string, id: string): number {
  return _.findIndex(content, c => c && c.contentType === type && c.id === id);
}

function updateContent(content: ReadonlyArray<IExplorableItemTypeInfo>,
                       type: ExplorableItemType,
                       id: string,
                       updateOptions: any
): ReadonlyArray<IExplorableItemTypeInfo> {
  const index = findIndexOfContent(content, type, id);
  if (index > -1) {
    const newContent = content.slice(0);
    newContent[index] = {
      ...content[index],
      ...updateOptions
    };
    return newContent;
  }
  return content;
}

function removeContentItem(
  content: ReadonlyArray<IExplorableItemTypeInfo>,
  type: ExplorableItemType,
  id: string
): ReadonlyArray<IExplorableItemTypeInfo> {
  const index = findIndexOfContent(content, type, id);
  if (index > -1) {
    const newContent = content.slice();
    newContent.splice(index, 1);
    return newContent;
  }
  return content;
}

export function contentReducer(
  previousState: ReadonlyArray<IExplorableItemTypeInfo> = [],
  action: Action = null
): ReadonlyArray<IExplorableItemTypeInfo> {
  if (isSpaceContentsRequestSucceededAction(action) && action.content) {
    // Don't set a new empty array as that will trigger renders.
    if (action.content.items.length === 0 && previousState.length === 0) {
      return previousState;
    }

    // We ignore any items in previous state and begin anew
    // We assume the slice starts with zero index so just extend
    // items array to match the total
    const emptyMissingItems =
      new Array(action.content.total - action.content.items.length);
    return action.content.items.concat(emptyMissingItems);

  } else if (isSpaceContentsSliceSucceededAction(action) && action.content) {
    if (previousState.length === action.content.total) {
      // if total items hasn't changed insert  newly retrieved items
      // into our existing items
      const items = previousState.slice();
      items.splice(action.start, action.content.items.length, ...action.content.items);
      return items;
    } else {
      // Total number of items doesn't match our state total
      // So we start over and insert our slice
      const items = new Array(action.content.total);
      items.splice(action.start, action.content.items.length, ...action.content.items);
      return items;
    }

  } else if (isContentFavoriteAddedAction(action) || isContentFavoriteRemovedAction(action)) {

    let contentId = action.contentId;
    //ugh.. if the current content is unifieddatasources, the id needs to be remaped from datasource to connection id
    if(previousState.length > 0 && DataSources.isUnifiedDatasource(previousState[0])){
      let content = _.find(previousState,
            c => c.contentType === action.contentType && (c as IDataSourceInfo).idForFavoriting === action.contentId);
      if(content){
        contentId = content.id;
      }
    }

    // if in Recommendation space page, keep favorite states of RecommendationCards updated
    if (previousState.length > 0 && isRecommendationType(previousState[0].contentType)) {
      let content = _.find(previousState, c => c.id === action.contentId);
      if (action.type === ContentFavoriteAdded && !(content as IContentInfo).favorite) {
        (content as IViewInfo).usageInfo.favoritesTotal += 1;
      } else if (action.type === ContentFavoriteRemoved && (content as IContentInfo).favorite) {
        (content as IViewInfo).usageInfo.favoritesTotal -= 1;
      }

      return updateContent(previousState, 'recommendedView', contentId, {
        favorite: action.type === ContentFavoriteAdded,
      });
    }

    return updateContent(previousState, action.contentType, contentId, {
      favorite: action.type === ContentFavoriteAdded
    });

  } else if (isRecommendationHiddenAction(action) || isRecommendationUnhiddenAction(action)) {
    let contentId = action.contentId;
    let contentType = action.contentType;

    return updateContent(previousState, contentType, contentId, {
      hide: action.type === RecommendationHidden
    });

  } else if (isUpdateContentDetailAction(action)) {
    let updatedContent = previousState;
    for(let content of action.content) {
      updatedContent = updateContent(updatedContent, content.contentType, content.id, {...content});
    }
    return updatedContent;

  }  else if (isMetricsValueRequestSucceededAction(action)) {
    if (action.metrics) {
      let parsedDataByIdMap: {[key: string]: ParsedMetricData } = {};

      for (let metric of action.metrics) {
        parsedDataByIdMap[metric.metric.luid] = metric.parsedData;
      }

      return previousState.map((oldItem) => {
        if (oldItem !== undefined && isMetricInfo(oldItem) && parsedDataByIdMap[oldItem.luid]) {
          const parsedData = parsedDataByIdMap[oldItem.luid];
          return {
            ...oldItem,
            metricValue: parsedData.initialValue,
            metricUpdated: parsedData.lastUpdatedTimestamp,
            parsedData
          };
        } else {
          return oldItem;
        }
      });
    }
  } else if (isSpaceContentsRequestFailedAction(action)) {
    // Don't set a new empty array as that will trigger renders.
    if (previousState.length === 0) {
      return previousState;
    }

    return [];
  }

  return previousState;
}
